import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer 
 * @property {HTMLElement} sku
 * @property {HTMLElement} availabilityContainer
 * @property {HTMLElement} availability
 * @property {HTMLElement} weightContainer
 * @property {HTMLElement} weight
 *
 * @extends {Component<Refs>}
 */
class VariantAttributesComponent extends Component {
	requiredRefs = ['skuContainer', 'sku', 'availabilityContainer', 'availability', 'weightContainer', 'weight'];

	connectedCallback() {
		super.connectedCallback();
		const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
		if (!target) return;
		target.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
		if (!target) return;
		target.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
	}

	/**
	 * @param {VariantUpdateEvent} event - The variant update event.
	 */
	updateSku = (event) => {
		if (event.detail.data.newProduct) {
			this.dataset.productId = event.detail.data.newProduct.id;
		}

		if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
			return;
		}

		// Use the variant data from the event
		// The variant is in event.detail.resource
		if (event.detail.resource) {
			const variantSku = event.detail.resource.sku || '';
			if (variantSku) {
				this.refs.skuContainer.style.display = 'block';
				this.refs.sku.textContent = variantSku;
			} else {
				this.refs.skuContainer.style.display = 'none';
				this.refs.sku.textContent = '';
			}

			const variantAvailabilityValue = event.detail.resource.available;
			const variantQuantity = event.detail.data.variantQuantity;
			let variantAvailability = ""
			if (variantAvailabilityValue && Number(variantQuantity) > 0) {
				const countText = Number(variantQuantity) === 1
					? (Theme?.translations?.inventory_item_count_one || "").replace('{{ count }}', String(variantQuantity))
					: (Theme?.translations?.inventory_item_count_other || "").replace('{{ count }}', String(variantQuantity));
				variantAvailability = `${countText} ${Theme?.translations?.inventory_in_stock}`
			} else if (variantAvailabilityValue && typeof variantQuantity !== 'undefined') {
				variantAvailability = Theme?.translations?.inventory_in_stock || 'In stock';
			} else if (!variantAvailabilityValue) {
				variantAvailability = Theme?.translations?.inventory_out_of_stock || 'Out of stock';
			}
			if (variantAvailability) {
				this.refs.availabilityContainer.style.display = 'block';
				this.refs.availability.textContent = variantAvailability;
			} else {
				this.refs.availabilityContainer.style.display = 'none';
				this.refs.availability.textContent = "";
			}

			const variantWeight = event.detail.resource.weight;
			if (variantWeight) {
				this.refs.weightContainer.style.display = 'block';
				this.refs.weight.textContent = String(variantWeight);
			} else {
				this.refs.weightContainer.style.display = 'none';
				this.refs.weight.textContent = '';
			}
		}
	};
}

if (!customElements.get('variant-attributes-component')) {
	customElements.define('variant-attributes-component', VariantAttributesComponent);
}
